/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { AnimationEvent } from '@angular/animations';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { CookieBannerConnectorService } from '@fleetmatics/ui.utilities/cookie-banner-connector';
import { MarkerClustererOptions } from '@googlemaps/markerclustererplus';
import { Action, select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, iif, merge, Observable, of, Subject, Subscription, timer } from 'rxjs';
import {
  concatMap,
  debounceTime,
  filter,
  first,
  map,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { isArrayNullOrEmpty, isElementNull, isStringNullOrEmpty } from '../../../../utils';
import {
  EDeviceStatusSnackbarTypes,
  getSnackbarError,
  getSnackbarSuccess,
  INotificationPreferences,
  SnackbarService
} from '../../../core/components';
import { ROUTE_PARAMS } from '../../../core/constants';
import {
  ECombinedPlotType,
  EMapContextMenuType,
  EMapTypeId,
  EPermissionStatus,
  EPlotIcon,
  EPlotType,
  EStartLocationSource,
  EStatusFilterType,
  EVehicleFuelType,
  EViewReplayType,
  HarshDrivingEventMarkerInfo,
  IAccountSettings,
  IAssetBalloon,
  IBalloon,
  IClusterBalloon,
  IClusterBalloonItem,
  IClusterBalloonRightClickParams,
  IClusterClickEvent,
  ICustomClickEventData,
  IEditPlaceParams,
  IFindNearestParams,
  IHarshDrivingEventBalloon,
  IIndexableObject,
  ILabel,
  ILatLng,
  IMapContextMenuData,
  IMaxPlottableMarkers,
  IPlaceBalloon,
  IPlotTypeDependency,
  IPoint,
  IPolylineHoverParams,
  IPolylineInfo,
  IScheduledJob,
  IScrollItem,
  IUserFeatures,
  IUserPermissions,
  IUserSettings,
  IVehicleWorkOrderBalloon,
  IViewGarminWindowParams,
  IViewMetricsParams,
  IViewReplayParameters,
  LatLng,
  LatLngBounds,
  MarkerInfo,
  MarkerLabel,
  Position,
  ReplayMarkerInfo,
  VehicleMarkerInfo,
  WorkOrderMarkerInfo
} from '../../../core/models';
import { IVideoDeviceInfo } from '../../../core/models/video-device-info.interface';
import { GoogleMapsService, WindowResizeService } from '../../../core/services';
import {
  ClearLabel,
  CloseBalloon,
  CloseVehicleContextMenu,
  GetClusterBalloon,
  GetLabel,
  GetLabels,
  GetLabelSuccess,
  GetReplayUserSettings,
  GetVehicleBalloonSuccess,
  HighlightMarker,
  MapBoundsUpdated,
  MapModuleLoaded,
  OpenVehicleContextMenu,
  PolylineClicked,
  PolylineEndHover,
  PolylineStartHover,
  RedirectToLocationHistory,
  RedirectToSchedulerPage,
  RemoveIndexLoaderIcon,
  RemoveMarkerHighlight,
  RemoveZoomToMarker,
  ReplayModuleLoaded,
  ScrollToItem,
  SelectVehicle,
  SetSelectedMarker,
  ShowBalloon,
  TriggerEditPlace,
  TriggerFindNearest,
  TriggerGetDirectionsTo,
  TriggerViewGarminWindow,
  TriggerViewMetrics,
  TriggerViewReplay,
  TryUpdateZoomToMarker,
  UpdateAnimationState,
  UpdateIsMapFitted,
  UpdateIsStreetViewOpen,
  UpdateMapCenter,
  UpdatePlotsInBounds,
  UpdateStreetViewLocation,
  ZoomLevelChanged
} from '../../../core/store/actions';
import { MapBalloonOpenWithDeviceHealthIssue } from '../../../core/store/actions/device-health.actions';
import {
  getAllDevicesHealth,
  getAnimationState,
  getAssetsMarkers,
  getBalloon,
  getContextMenu,
  getDevicehealthDetails,
  getHarshDrivingEventMarker,
  getIsClusteringByDefaultFeatureToggleEnabled,
  getIsClusteringEnabled,
  getIsCustomBalloonsFeatureEnabled,
  getIsFullscreen,
  getIsGarminStopsEnabled,
  getIsGeofencesEnabled,
  getIsMapFitted,
  getIsReplayOpen,
  getIsSelfServiceExperimentIconWithErrorEnabled,
  getIsShowLabelsEnabled,
  getIsStreetViewOpen,
  getIsSuggestedGeofencesEnabled,
  getIsTrafficEnabled,
  getIsVehicleDriverPanelCollapsed,
  getIsWorkOrdersEnabled,
  getLabel,
  getLabels,
  getLayoutMargins,
  getMapChangeBounds,
  getMapGarminStopMarkers,
  getMapGeofenceMarkers,
  getMapHotspotsMarkers,
  getMapType,
  getMapVehicleMarkersFilteredByStatusType,
  getMapWorkOrderMarkers,
  getMarkerToHighlight,
  getReplayModuleLoaded,
  getSelectedMarker,
  getSelectedVehicleIdDeviceHealth,
  getShouldShowVehicleBalloonEcm,
  getShouldShowVehicleBalloonLinks,
  getShowStartingLocation,
  getStreetViewLocation,
  getTriggerFitToMap,
  getUserAccountSettings,
  getUserFeatures,
  getUserMaxPlottableMarkers,
  getUserNotificationPreferences,
  getUserPermissions,
  getUserSettings,
  getVehicleDriverPanelModuleLoaded,
  getVehicleFuelTypesFromHierarchy,
  getVehiclePlots,
  getVehiclePlotsTimers,
  getVehicleStatusFilterType,
  getWorkingTimeDirectiveDisplayEnabled,
  getZoomToLocation,
  getZoomToMarker
} from '../../../core/store/selectors';
import { getVideoDeviceInfoState } from '../../../core/store/selectors/video-device-info.selectors';
import { EMapAnimationState, IAppState, IDeviceHealth, ILayoutPreferencesState } from '../../../core/store/state';
import { EGeofenceDrawingControlShape } from '../../../geofence/models';
import {
  getGeofenceCurrentShape,
  getGeofenceDrawingControlClearPressed,
  getGeofenceDrawingControlShape,
  getGeofenceLatLng,
  getIsGeofenceCreateOpen,
  SetSelectedGeofenceShape
} from '../../../geofence/store';
import { LazyLoaderService } from '../../../lazy-loader';
import {
  EndReplayPlotAnimation,
  getHoveredPolyline,
  getReplayMarkers,
  getReplayPolylineDayLabels,
  getReplayPolylines,
  getShouldAnimatePlots,
  getVideoRequestedStatus,
  NextPlotInJourney,
  PreviousPlotInJourney,
  RequestVideoInJourney
} from '../../../replay/store';
import { MapAnimations } from '../../animations';
import { GoogleMapsShape } from '../../models';
import { GoogleMapsLoaderService } from '../../services';
import { ContextMenuDataLoaderService } from '../../services/context-menu-data-loader';
import { trackableClusterOptions, workOrderClusterOptions } from './map-cluster-configs.constants';
import { plotTypeConfigs } from './map-plot-configs.constants';
import { VideoRequestModalComponent } from './video-request-modal/video-request-modal.component';

@Component({
  selector: 'fm-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [MapAnimations.replayMapOverlayHide, MapAnimations.replayPanel]
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  public plotTypeDependencies: IPlotTypeDependency[] = [
    {
      plotTypeOrigin: EPlotType.vehicle,
      plotTypeTarget: EPlotType.workOrder,
      originLinkValue: (markerInfo: MarkerInfo) => (markerInfo as VehicleMarkerInfo).driverId,
      targetLinkValue: (markerInfo: MarkerInfo) => (markerInfo as WorkOrderMarkerInfo).driverId
    },
    {
      plotTypeOrigin: EPlotType.workOrder,
      plotTypeTarget: EPlotType.vehicle,
      originLinkValue: (markerInfo: MarkerInfo) => (markerInfo as WorkOrderMarkerInfo).driverId,
      targetLinkValue: (markerInfo: MarkerInfo) => (markerInfo as VehicleMarkerInfo).driverId
    },
    {
      plotTypeOrigin: EPlotType.poweredAsset,
      plotTypeTarget: EPlotType.workOrder,
      originLinkValue: (markerInfo: MarkerInfo) => (markerInfo as VehicleMarkerInfo).driverId,
      targetLinkValue: (markerInfo: MarkerInfo) => (markerInfo as WorkOrderMarkerInfo).driverId
    },
    {
      plotTypeOrigin: EPlotType.workOrder,
      plotTypeTarget: EPlotType.poweredAsset,
      originLinkValue: (markerInfo: MarkerInfo) => (markerInfo as WorkOrderMarkerInfo).driverId,
      targetLinkValue: (markerInfo: MarkerInfo) => (markerInfo as VehicleMarkerInfo).driverId
    }
  ];

  public plotTypeConfigs = plotTypeConfigs;
  public EPermissionStatus = EPermissionStatus;
  public EPlotType = EPlotType;
  public isStreetViewAvailableForMarker: boolean;
  public balloon$: Observable<IBalloon>;
  public isFullscreen$: Observable<boolean>;
  public vehicleMarkers$: Observable<VehicleMarkerInfo[]>;
  public devicesIssues$: Observable<IDeviceHealth[]>;
  public hotspotsMarkers$: Observable<MarkerInfo[]>;
  public geofenceMarkers$: Observable<MarkerInfo[]>;
  public garminStopMarkers$: Observable<MarkerInfo[]>;
  public workOrdersMarkers$: Observable<WorkOrderMarkerInfo[]>;
  public harshDrivingEventMarker$: Observable<HarshDrivingEventMarkerInfo>;
  public assetsMarkers$: Observable<MarkerInfo[]>;
  public replayMarkers$: Observable<ReplayMarkerInfo[]>;
  public replayPolylines$: Observable<IPolylineInfo[]>;
  public hoveredPolyline$: Observable<IPolylineHoverParams>;
  public isMapFitted$: Observable<boolean>;
  public triggerFitToMap$: Observable<Object>;
  public isStreetViewOpen$: Observable<boolean>;
  public layoutPreferences$: Observable<ILayoutPreferencesState>;
  public mapType$: Observable<EMapTypeId>;
  public userSettings$: Observable<IUserSettings>;
  public markers$: Observable<MarkerInfo[]>;
  public isSuggestedGeofenceEnabled$: Observable<boolean>;
  public isGeofenceEnabled$: Observable<boolean>;
  public isGarminStopsEnabled$: Observable<boolean>;
  public isWorkOrdersEnabled$: Observable<boolean>;
  public maxPlottableMarkers$: Observable<IMaxPlottableMarkers[]>;
  public isTrafficEnabled$: Observable<boolean>;
  public isClusteringEnabled$: Observable<boolean>;
  public isEnhancedClusteringEnabled$: Observable<boolean>;
  public isShowLabelsEnabled$: Observable<boolean>;
  public markersLabels$: Observable<MarkerLabel[]>;
  public markerLabel$: Observable<MarkerLabel>;
  public overlayMargins$: Observable<Position>;
  public isSnackbarOpen: boolean;
  public mapContextMenuType: EMapContextMenuType;
  public mapContextMenuData: IMapContextMenuData<MarkerInfo>;
  public streetViewLocation$: Observable<ILatLng>;
  public zoomToLocation$: Observable<ILatLng>;
  public zoomToMarker$: Observable<ILatLng>;
  public plotClusterConfig$: BehaviorSubject<Map<MarkerClustererOptions, EPlotType[]>> = new BehaviorSubject<
    Map<MarkerClustererOptions, EPlotType[]>
  >(new Map<MarkerClustererOptions, EPlotType[]>());
  public plotTypesToShowLabel: EPlotType[] = [];
  public vehicleStatusFilterSelected$: Observable<EStatusFilterType>;
  public notificationPreferences$: Observable<INotificationPreferences[]>;
  public userPermissions$: Observable<IUserPermissions>;
  public userFeatures$: Observable<IUserFeatures>;
  public snackbarSuccess$: Subscription;
  public snackbarError$: Subscription;
  public snackbarConfirmedAs: string;
  public deviceStatusSnackbarTypes = EDeviceStatusSnackbarTypes;
  public vehiclePlotsTimers$: Observable<IIndexableObject<number>>;
  public userAccountSettings$: Observable<IAccountSettings>;
  public mapStartingLocation$: Observable<LatLng[]>;
  public markerToHighlight$: Observable<MarkerInfo>;
  public selectedMarker$: Observable<MarkerInfo>;
  public showTachoInformation$: Observable<boolean>;
  public showVehicleBalloonLinks$: Observable<boolean>;
  public customBalloonFeatureEnabled$: Observable<boolean>;
  public showVehicleBalloonEcm$: Observable<boolean>;
  public isReplayOpen$: Observable<boolean>;
  public animationState$: Observable<EMapAnimationState>;
  public mapBounds$: Observable<ILatLng[]>;
  public isVehicleDriverPanelCollapsed$: Observable<boolean>;
  public tilesLoaded$: Observable<boolean>;
  public vehicleFuelTypes$: Observable<Map<number, EVehicleFuelType>>;
  public isCookieAccepted: boolean;
  public isVehicleDriverPanelLoaded$: Observable<boolean>;
  public clusterBalloonZoomToClicked$ = new Subject<IClusterBalloon>();
  public zoomToZoomLevel = 18;
  public disableZoomTo = false;
  public vehicleLastStateChangeDuration$: Observable<number>;
  public isReplayModuleLoaded$: Observable<boolean>;
  public geofenceDrawingShape$: Observable<EGeofenceDrawingControlShape | null>;
  public geofenceShape$: Observable<GoogleMapsShape | null>;
  public geofenceDrawingShapeLatLng$: Observable<LatLng | null>;
  public isGeofenceCreateOpen$: Observable<boolean>;
  public geofenceDrawingControlClearPressed$: Observable<Date>;
  public videoDeviceInfo$: Observable<IVideoDeviceInfo>;
  public vehicleDeviceHealthStatus$: Observable<IDeviceHealth>;
  public replayAnimationState = 'default';
  public EMapAnimationState = EMapAnimationState;
  public showReplaySidePanel = false;
  public showPlotIconsWithError$: Observable<boolean>;

  public clusterConfig = new Map<MarkerClustererOptions, EPlotType[]>([
    [trackableClusterOptions, [EPlotType.vehicle, EPlotType.poweredAsset, EPlotType.asset]],
    [workOrderClusterOptions, [EPlotType.workOrder]]
  ]);

  @ViewChild('maxPlottableWarningTemplate')
  private readonly _maxPlottableWarningTemplate: TemplateRef<any>;

  @ViewChild('snackbarSuccessTemplate')
  private readonly _snackbarSuccess: TemplateRef<any>;

  @ViewChild('snackbarErrorTemplate')
  private readonly _snackbarError: TemplateRef<any>;

  private _balloon: IBalloon;

  private _hasBoundsChangedSinceMaxPlottableWarningWasShown: boolean;

  private readonly _unsubscribe$ = new Subject<void>();
  private readonly _polylineHovers$ = new Subject<Action>();
  private readonly _mapBoundsUpdated$ = new Subject<Action>();
  private readonly _plotChanges$ = new Subject<Function>();
  private readonly _tilesLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly _defaultMobileOverlayMargins = new Position(56, 0, 80, 0);

  private readonly _defaultDesktopMargins = new Position(56, 50, 64, 320);

  constructor(
    private readonly _store: Store<IAppState>,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _snackbarService: SnackbarService,
    private readonly _googleMapsService: GoogleMapsService,
    private readonly _cookieBannerService: CookieBannerConnectorService,
    private readonly _googleMapsLoaderService: GoogleMapsLoaderService,
    private readonly _lazyLoadModuleService: LazyLoaderService,
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _windowResizeService: WindowResizeService,
    private readonly _contextMenuDataLoaderService: ContextMenuDataLoaderService,
    private readonly _dialog: MatLegacyDialog,
    private readonly _route: ActivatedRoute,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  public ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this._googleMapsLoaderService.loadMapOnly(environment.config.googleMapsClientId, environment.config.googleMapsApiVersion);
    }

    this._getMapSelectors();

    this.markers$ = this._setUpMarkers();

    combineLatest([this.isClusteringEnabled$, this.isReplayOpen$])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([isClusteringEnabled, isReplayOpen]) => {
        if (isClusteringEnabled && !isReplayOpen) {
          this._enableClusteringOnMap();
        } else {
          this._disableClusteringOnMap();
        }
      });

    combineLatest([this.isShowLabelsEnabled$, this.isReplayOpen$])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([isShowLabelsEnabled, isReplayOpen]) => {
        if (isShowLabelsEnabled && !isReplayOpen) {
          this.plotTypesToShowLabel = [EPlotType.vehicle, EPlotType.asset, EPlotType.poweredAsset];
        } else if (isReplayOpen) {
          this.plotTypesToShowLabel = [EPlotType.polylineDayDot];
        } else {
          this.plotTypesToShowLabel = [];
        }
      });

    this._cookieBannerService.isAccepted$
      .pipe(
        filter(isAccepted => isAccepted),
        take(1)
      )
      .subscribe(value => {
        this.isCookieAccepted = value;
      });

    this._setupReplayAnimationState();

    this._polylineHovers$.pipe(takeUntil(this._unsubscribe$), debounceTime(50)).subscribe(action => this._store.dispatch(action));
    this._mapBoundsUpdated$.pipe(takeUntil(this._unsubscribe$), debounceTime(50)).subscribe(action => this._store.dispatch(action));
    this._plotChanges$.pipe(takeUntil(this._unsubscribe$), debounceTime(200)).subscribe(func => func());

    /* set a default value for margins to avoid the map changing center after overlays are loaded
       overlays might be different than the default, in which case there will be a "flicker",
       however, most of the times, the margins are the same as the default.
       example for being different - cookie has layout preferences saved - if vehicle driver panel is collapsed
    */
    this.overlayMargins$ = this._windowResizeService.isMobile$.pipe(
      map(isMobile => (isMobile ? this._defaultMobileOverlayMargins : this._defaultDesktopMargins)),
      switchMap(initialMargins =>
        this._store.pipe(
          select(getLayoutMargins),
          map((margins, index) => {
            // this is not an ideal solution but otherwise the margins update as modules are loaded and
            // the user experience for the initializing is bad
            const modulesThatAreLoadedOnInit = 3;
            return index < modulesThatAreLoadedOnInit ? initialMargins : margins;
          })
        )
      )
    );

    this.isVehicleDriverPanelLoaded$ = this._store.pipe(select(getVehicleDriverPanelModuleLoaded));
    this.isReplayModuleLoaded$ = this._store.pipe(select(getReplayModuleLoaded));
    this.geofenceDrawingShape$ = this._store.pipe(select(getGeofenceDrawingControlShape));
    this.geofenceDrawingShapeLatLng$ = this._store.pipe(select(getGeofenceLatLng));
    this.geofenceShape$ = this._store.pipe(select(getGeofenceCurrentShape));
    this.isGeofenceCreateOpen$ = this._store.pipe(select(getIsGeofenceCreateOpen));
    this.geofenceDrawingControlClearPressed$ = this._store.pipe(select(getGeofenceDrawingControlClearPressed));
    this.videoDeviceInfo$ = this._store.pipe(select(getVideoDeviceInfoState));
    this._store.pipe(select(getSelectedVehicleIdDeviceHealth), takeUntil(this._unsubscribe$)).subscribe(vehicleId => {
      if (vehicleId != '') {
        this.vehicleDeviceHealthStatus$ = this._store.pipe(select(getDevicehealthDetails));
        this._store.dispatch(new MapBalloonOpenWithDeviceHealthIssue(vehicleId));
      }
    });

    this.showPlotIconsWithError$ = this.userPermissions$.pipe(
      withLatestFrom(this._store.pipe(select(getIsSelfServiceExperimentIconWithErrorEnabled))),
      filter(([permissions, featureEnabled]) => !isElementNull(permissions) && !isElementNull(featureEnabled)),
      map(
        ([permissions, featureEnabled]) =>
          featureEnabled &&
          (permissions.manageVehicles == EPermissionStatus.Full ||
            permissions.manageAssets == EPermissionStatus.Full ||
            permissions.videoExperience == EPermissionStatus.Full)
      )
    );
  }

  public ngAfterViewInit(): void {
    this.tilesLoaded$.pipe(take(1)).subscribe(() => {
      this._store.dispatch(new MapModuleLoaded());
      this._loadReplayWhenDriverPanelLoaded();
    });
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onBalloonClosed(): void {
    this._store.dispatch(new CloseBalloon());
  }

  public onFitToMapChanges(isMapFitted: boolean): void {
    this._store.dispatch(new UpdateIsMapFitted(isMapFitted));
  }

  public onRemoveZoomToMarker(): void {
    this._store.dispatch(new RemoveZoomToMarker());
  }

  public onBoundsChanges(bounds: google.maps.LatLngBounds): void {
    this._hasBoundsChangedSinceMaxPlottableWarningWasShown = true;
    const mapBounds = LatLngBounds.fromGoogleMaps(bounds);
    this._mapBoundsUpdated$.next(new MapBoundsUpdated(mapBounds));
  }

  public onPlotsInBoundsChanges(plotsInBounds: MarkerInfo[]): void {
    this._store.dispatch(new UpdatePlotsInBounds(plotsInBounds));
    this.maxPlottableMarkers$.pipe(takeUntil(this._unsubscribe$)).subscribe(plottableMarker => {
      if (!isArrayNullOrEmpty(plottableMarker)) {
        const maxBalloonPlots = plottableMarker.find(marker => marker.type === EPlotType.balloon).max;
        if (plotsInBounds.length <= maxBalloonPlots) {
          this._store.dispatch(new GetLabels(plotsInBounds));
        } else {
          this._store.dispatch(new GetLabels([]));
        }
      }
    });
  }

  public onCurrentPlotChanges(currentPlot: MarkerInfo): void {
    this._plotChanges$.next(() => {
      if (!isElementNull(currentPlot)) {
        if (currentPlot.type !== EPlotType.geofence && currentPlot.type !== EPlotType.garmin && currentPlot.type !== EPlotType.asset) {
          this._store.dispatch(new GetLabel([currentPlot]));
        } else {
          this._store.dispatch(
            new GetLabelSuccess({
              id: currentPlot.id,
              isMapLabel: true,
              mapEntityType: currentPlot.type,
              customElements: [currentPlot.title]
            })
          );
        }
      } else {
        this._store.dispatch(new ClearLabel());
      }
    });
  }

  public onCurrentPlotStartHover(currentPlot: MarkerInfo): void {
    if (!isElementNull(currentPlot)) {
      this._store.dispatch(new HighlightMarker(currentPlot.id));
    }
  }

  public onCurrentPlotEndHover(): void {
    this._store.dispatch(new RemoveMarkerHighlight());
  }

  public onStreetViewChange(event: boolean): void {
    this._store.dispatch(new UpdateIsStreetViewOpen(event));
  }

  public onMapCenterChange(latLng: LatLng): void {
    this._store.dispatch(new UpdateMapCenter(latLng));
  }

  public showSnackbar() {
    if (!this._hasBoundsChangedSinceMaxPlottableWarningWasShown) {
      return;
    }
    this._snackbarService.open(this._maxPlottableWarningTemplate, 4000);
    this._hasBoundsChangedSinceMaxPlottableWarningWasShown = false;
  }

  public showMapContextMenu(menuData: IMapContextMenuData<MarkerInfo>) {
    this._closeMapContextMenu();
    this.mapContextMenuData = menuData;
    this.mapContextMenuType = EMapContextMenuType.Map;
    this._changeDetectorRef.detectChanges();
  }

  public showMarkerContextMenu(menuData: IMapContextMenuData<MarkerInfo>) {
    this._closeMapContextMenu();
    this._googleMapsService
      .isStreetViewAvailable(menuData.clickLatLng.toGoogleMaps())
      .pipe(take(1))
      .subscribe(isAvailable => {
        menuData.streetViewAvailable = isAvailable;
        this.mapContextMenuData = menuData;
        switch (menuData.markerInfo.type) {
          case EPlotType.vehicle:
          case EPlotType.poweredAsset:
            this.mapContextMenuType = EMapContextMenuType.Vehicle;
            break;
          case EPlotType.geofence:
            this.mapContextMenuType = EMapContextMenuType.Geofence;
            break;
          case EPlotType.hotspot:
            this.mapContextMenuType = EMapContextMenuType.Hotspot;
            break;
          case EPlotType.garmin:
            this.mapContextMenuType = EMapContextMenuType.Garmin;
            break;
          case EPlotType.workOrder:
            this.mapContextMenuType = EMapContextMenuType.WorkOrder;
            break;
          case EPlotType.asset:
            this.mapContextMenuType = EMapContextMenuType.Asset;
            break;
        }
        this._changeDetectorRef.detectChanges();
      });
  }

  public onMapContextMenuClosed() {
    this._closeMapContextMenu();
  }

  public onMarkerClick(clickData: ICustomClickEventData<MarkerInfo>): void {
    if (!isElementNull(this._balloon) && this._balloon.id === clickData.data.id && this._balloon.type === clickData.data.type) {
      this._store.dispatch(new CloseBalloon());
    } else {
      this._store.dispatch(new ShowBalloon(clickData));
      this._store.dispatch(new ScrollToItem({ id: clickData.data.id, type: clickData.data.type } as IScrollItem));
    }
    this._store.dispatch(new SetSelectedMarker(clickData.data.id));
  }

  public onBalloonBackToVideo(balloon: IHarshDrivingEventBalloon): void {
    this._document.defaultView.location.href = balloon.eventPage;
  }

  public onVehicleReplay(id: number): void {
    const viewReplayParameters: IViewReplayParameters = { id, type: EViewReplayType.Vehicle };
    this._store.dispatch(new TriggerViewReplay(viewReplayParameters));
  }

  public onAssetReplay(id: number): void {
    const viewReplayParameters: IViewReplayParameters = { id, type: EViewReplayType.Asset };
    this._store.dispatch(new TriggerViewReplay(viewReplayParameters));
  }

  public onHarshDrivingEventReplay(balloon: IHarshDrivingEventBalloon): void {
    const viewReplayParameters: IViewReplayParameters = {
      id: balloon.id,
      type: EViewReplayType.Vehicle,
      dateTime: balloon.date
    };
    this._store.dispatch(new TriggerViewReplay(viewReplayParameters));
  }

  public onPlaceBalloonEditClicked(balloon: IPlaceBalloon): void {
    const params: IEditPlaceParams = {
      id: balloon.id,
      type: balloon.placeType
    };
    this._store.dispatch(new CloseBalloon());
    this._store.dispatch(new TriggerEditPlace(params));
  }

  public onBalloonFindNearestClicked(balloon: IBalloon): void {
    const params: IFindNearestParams = {
      m: '2',
      v: [],
      np: `(${balloon.latLng.lat}, ${balloon.latLng.lng})`
    };
    this._store.dispatch(new TriggerFindNearest(params));
  }

  public onPlaceBalloonViewMetricsClicked(balloon: IPlaceBalloon): void {
    const params: IViewMetricsParams = {
      id: balloon.id,
      type: balloon.placeType,
      sm: 1
    };
    this._store.dispatch(new TriggerViewMetrics(params));
  }

  public onVehicleWorkOrderBalloonShowMessage(balloon: IVehicleWorkOrderBalloon): void {
    const params: IViewGarminWindowParams = {
      uiMode: '2',
      vehicleId: balloon.id
    };
    this._store.dispatch(new TriggerViewGarminWindow(params));
  }

  public onBalloonStreetViewClicked(balloon: IBalloon): void {
    this._store.dispatch(new UpdateStreetViewLocation(balloon.latLng));
  }

  public onBalloonPreviousPlotInJourney(balloon: IBalloon): void {
    this._store.dispatch(new PreviousPlotInJourney(balloon.id));
  }

  public onBalloonRequestVideoInJourney(event: any): void {
    const startTimeOffsetInMilliseconds = 5000;
    const endTimeOffsetInMilliseconds = 35000;
    this._store.dispatch(
      new RequestVideoInJourney({
        startTimeUtc: new Date(event.startTime.getTime() - startTimeOffsetInMilliseconds),
        endTimeUtc: new Date(event.startTime.getTime() + endTimeOffsetInMilliseconds),
        online: false
      })
    );
    this._store
      .pipe(
        select(getVideoRequestedStatus),
        filter(({ requesting }) => !requesting),
        first()
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(({ requested }) => {
        const shouldReturnBackToVideo = this._route.snapshot.queryParamMap.has(ROUTE_PARAMS.REPLAY.VEHICLE_ID);
        if (requested) {
          this._dialog.open(VideoRequestModalComponent, {
            maxWidth: '608px',
            data: {
              returnBackToVideo: shouldReturnBackToVideo,
              videoDeviceInfo: this.videoDeviceInfo$
            },
            autoFocus: false,
            panelClass: 'video-request-dialog'
          });
        }
      });
  }

  public onBalloonNextPlotInJourney(balloon: IBalloon): void {
    this._store.dispatch(new NextPlotInJourney(balloon.id));
  }

  public onBalloonZoomTo(balloon: IVehicleWorkOrderBalloon): void {
    this._store
      .pipe(
        select(getVehiclePlots),
        take(1),
        map(vehiclePlots => vehiclePlots.find(plot => plot.id === balloon.id))
      )
      .subscribe(plot => {
        this._store.dispatch(
          new TryUpdateZoomToMarker({
            latLng: balloon.latLng,
            id: balloon.id,
            type: plot.type,
            title: balloon.vehicleName
          })
        );
      });
  }

  public onAssetBalloonZoomTo(balloon: IAssetBalloon): void {
    this._store.dispatch(
      new TryUpdateZoomToMarker({
        latLng: balloon.latLng,
        id: balloon.id,
        type: EPlotType.asset,
        title: balloon.name
      })
    );
  }

  public onBalloonGetDirectionsTo(balloon: IBalloon): void {
    this._store.dispatch(new TriggerGetDirectionsTo(balloon.latLng));
  }

  public onBalloonLocationHistory(assetId: number): void {
    this._store.dispatch(new RedirectToLocationHistory(assetId));
  }

  public showDeviceStatusOnMap(vehicleId: string) {
    this._store.dispatch(new SelectVehicle(`v${vehicleId}`));
  }

  public onScheduledJobClicked(scheduledJob: IScheduledJob) {
    this._store.dispatch(new RedirectToSchedulerPage(scheduledJob));
  }

  public vehicleWorkOrderBalloonResized(balloon: IVehicleWorkOrderBalloon): void {
    this._store.dispatch(new CloseBalloon());
    setTimeout(() => {
      this._store.dispatch(new GetVehicleBalloonSuccess(balloon));
    }, 0);
  }

  public getBalloonTimer(balloonId: number): Observable<number> {
    return this.vehiclePlotsTimers$.pipe(map((timers: IIndexableObject<number>) => timers[balloonId]));
  }

  public onReplayAnimationDone(event: AnimationEvent): void {
    if (event.fromState === 'void') {
      this._store.dispatch(new UpdateAnimationState(EMapAnimationState.replayPanelOpen));
    } else if (event.toState === 'void' && this.replayAnimationState === EMapAnimationState.replayPanelClosed) {
      this._store.dispatch(new UpdateAnimationState(EMapAnimationState.overlayHidden));
    }
  }

  public onReplayAnimationStart(event: AnimationEvent): void {
    if (event.toState === 'void') {
      this._store.dispatch(new UpdateAnimationState(EMapAnimationState.replayPanelClosed));
    }
  }

  public onPolylineStartHover(polylineHoverParams: IPolylineHoverParams): void {
    this._polylineHovers$.next(new PolylineStartHover(polylineHoverParams));
  }

  public onPolylineEndHover(): void {
    this._polylineHovers$.next(new PolylineEndHover());
  }

  public onPolylineClicked(polylineInfo: IPolylineInfo): void {
    this._store.dispatch(new PolylineClicked(polylineInfo));
  }

  public onZoomLevelChanged(zoomLevel: number) {
    this.disableZoomTo = zoomLevel >= this.zoomToZoomLevel;
    this._store.dispatch(new ZoomLevelChanged(zoomLevel));
  }

  public onMapTilesLoadedFirstTime() {
    this._tilesLoadedSubject.next(true);
  }

  public clusterClicked(event: IClusterClickEvent) {
    if (!isElementNull(this._balloon) && this._balloon.id === event.id && this._balloon.type === EPlotType.cluster) {
      this._store.dispatch(new CloseBalloon());
    } else {
      this._store.dispatch(new GetClusterBalloon(event));
    }
  }

  public onClusterBalloonZoomTo(clusterBalloon: IClusterBalloon) {
    this.clusterBalloonZoomToClicked$.next(clusterBalloon);
  }

  public onClusterBalloonItemClicked(clusterBalloonItem: IClusterBalloonItem) {
    this._store.dispatch(
      new TryUpdateZoomToMarker({
        latLng: clusterBalloonItem.latLng,
        id: clusterBalloonItem.id,
        type: clusterBalloonItem.type,
        title: clusterBalloonItem.label
      })
    );
    this._store.dispatch(new ScrollToItem({ id: clusterBalloonItem.id, type: clusterBalloonItem.type } as IScrollItem));
  }

  public onClusterBalloonItemRightClicked(clusterBalloonRightClickParams: IClusterBalloonRightClickParams) {
    const clickPosition = clusterBalloonRightClickParams.clickPosition;
    const clusterBalloonItem: IClusterBalloonItem = clusterBalloonRightClickParams.clusterBalloonItem;

    const clusterBalloonContextMenuData: IMapContextMenuData<MarkerInfo> = this._getContextMenuData(clusterBalloonItem, clickPosition);
    this._store.dispatch(new OpenVehicleContextMenu(clusterBalloonContextMenuData));
  }

  public mapLoaded() {
    this._store.dispatch(new RemoveIndexLoaderIcon());
  }

  public geofenceShapeChanged(shape: GoogleMapsShape) {
    this._store.dispatch(new SetSelectedGeofenceShape(shape));
  }

  private _getContextMenuData(clusterBalloonItem: IClusterBalloonItem, clickPosition: IPoint): IMapContextMenuData<MarkerInfo> {
    if (clusterBalloonItem.type === EPlotType.asset) {
      return this._contextMenuDataLoaderService.getAssetContextMenuData(clusterBalloonItem, clickPosition);
    }
    return this._contextMenuDataLoaderService.getVehicleContextMenuData(clusterBalloonItem, clickPosition);
  }

  private _closeMapContextMenu(): void {
    if (!isElementNull(this.mapContextMenuType)) {
      this._store.dispatch(new CloseVehicleContextMenu());
    }
    this.mapContextMenuType = null;
    this._changeDetectorRef.detectChanges();
  }

  private _getMapSelectors(): void {
    this.tilesLoaded$ = this._tilesLoadedSubject.asObservable().pipe(
      filter(isLoaded => isLoaded),
      take(1),
      map(() => true),
      shareReplay(1)
    );

    this.balloon$ = this._store.pipe(
      select(getBalloon),
      tap(balloon => {
        this._balloon = balloon;
        if (isElementNull(balloon)) {
          return;
        }
        this._googleMapsLoaderService.googleMapsLoaded$.pipe(filter(isLoaded => isLoaded)).subscribe(() => {
          this._googleMapsService
            .isStreetViewAvailable(balloon.latLng.toGoogleMaps())
            .pipe(take(1))
            .subscribe(isAvailable => {
              this.isStreetViewAvailableForMarker = isAvailable;
            });
        });
      })
    );

    this.vehicleLastStateChangeDuration$ = this._store.pipe(
      select(getVehiclePlots),
      withLatestFrom(this._store.pipe(select(getBalloon))),
      map(([plots, balloon]) => {
        const vehiclePlot = plots.find(plot => plot.id === balloon.id);
        if (!isElementNull(vehiclePlot) && !isElementNull(vehiclePlot?.duration)) {
          return vehiclePlot.duration;
        } else {
          return null;
        }
      })
    );
    this.isReplayOpen$ = this._store.pipe(select(getIsReplayOpen));
    this.animationState$ = this._store.pipe(select(getAnimationState));
    this.isFullscreen$ = this._store.pipe(select(getIsFullscreen));
    this.isStreetViewOpen$ = this._store.pipe(select(getIsStreetViewOpen));
    this.vehicleMarkers$ = this._store.pipe(select(getMapVehicleMarkersFilteredByStatusType));
    this.devicesIssues$ = this._store.pipe(select(getAllDevicesHealth));
    this.isMapFitted$ = this._store.pipe(
      select(getIsMapFitted),
      withLatestFrom(this.isReplayOpen$),
      map(([isMapFitted, isReplayOpen]) => !isReplayOpen && isMapFitted)
    );
    this.triggerFitToMap$ = this._store.pipe(select(getTriggerFitToMap));
    this.mapType$ = this._store.pipe(select(getMapType));
    this.userSettings$ = this._store.pipe(select(getUserSettings));
    this.isSuggestedGeofenceEnabled$ = this._store.pipe(select(getIsSuggestedGeofencesEnabled));
    this.isGeofenceEnabled$ = this._store.pipe(select(getIsGeofencesEnabled));
    this.isGarminStopsEnabled$ = this._store.pipe(select(getIsGarminStopsEnabled));
    this.isWorkOrdersEnabled$ = this._store.pipe(select(getIsWorkOrdersEnabled));
    this.hotspotsMarkers$ = this._store.pipe(select(getMapHotspotsMarkers));
    this.geofenceMarkers$ = this._store.pipe(select(getMapGeofenceMarkers));
    this.garminStopMarkers$ = this._store.pipe(select(getMapGarminStopMarkers));
    this.workOrdersMarkers$ = this._store.pipe(select(getMapWorkOrderMarkers));
    this.harshDrivingEventMarker$ = this._store.pipe(select(getHarshDrivingEventMarker));
    this.assetsMarkers$ = this._store.pipe(select(getAssetsMarkers));
    this.replayMarkers$ = this._store.pipe(select(getReplayMarkers));
    this.replayPolylines$ = this._store.pipe(select(getReplayPolylines), debounceTime(50));
    this.hoveredPolyline$ = this._store.pipe(select(getHoveredPolyline));
    this.maxPlottableMarkers$ = this._store.pipe(select(getUserMaxPlottableMarkers));
    this.isTrafficEnabled$ = this._store.pipe(select(getIsTrafficEnabled));
    this.streetViewLocation$ = this._store.pipe(select(getStreetViewLocation));
    this.isClusteringEnabled$ = this._store.pipe(select(getIsClusteringEnabled));
    this.isEnhancedClusteringEnabled$ = this._store.pipe(select(getIsClusteringByDefaultFeatureToggleEnabled));
    this.isShowLabelsEnabled$ = this._store.pipe(select(getIsShowLabelsEnabled));
    this.markersLabels$ = combineLatest([this._store.pipe(select(getLabels)), this._store.pipe(select(getReplayPolylineDayLabels))]).pipe(
      map(([labels, polylineLabels]) => labels?.concat(polylineLabels).map(label => this._transformLabelMarker(label)))
    );
    this.markerLabel$ = this._store.pipe(
      select(getLabel),
      map(label => this._transformLabelMarker(label))
    );
    this.zoomToLocation$ = this._store.pipe(select(getZoomToLocation));
    this.zoomToMarker$ = this._store.pipe(select(getZoomToMarker));
    this.vehicleStatusFilterSelected$ = this._store.pipe(select(getVehicleStatusFilterType));
    this.notificationPreferences$ = this._store.pipe(select(getUserNotificationPreferences));
    this.userPermissions$ = this._store.pipe(select(getUserPermissions));
    this.userFeatures$ = this._store.pipe(select(getUserFeatures));
    this.vehiclePlotsTimers$ = this._store.pipe(select(getVehiclePlotsTimers));
    this.userAccountSettings$ = this._store.pipe(select(getUserAccountSettings));
    this.showTachoInformation$ = this._store.pipe(select(getWorkingTimeDirectiveDisplayEnabled));
    this.showVehicleBalloonLinks$ = this._store.pipe(select(getShouldShowVehicleBalloonLinks));
    this.customBalloonFeatureEnabled$ = this._store.pipe(select(getIsCustomBalloonsFeatureEnabled));
    this.showVehicleBalloonEcm$ = this._store.pipe(select(getShouldShowVehicleBalloonEcm));
    this.isVehicleDriverPanelCollapsed$ = this._store.pipe(select(getIsVehicleDriverPanelCollapsed));
    this.mapBounds$ = this._store.pipe(
      select(getMapChangeBounds),
      filter(latLngs => !isElementNull(latLngs)),
      skip(1) // skip the first emission - first change bounds is handled by a trigger from mapStartingLocation$
    );
    this.mapStartingLocation$ = this._getStartLocationObservable();
    this.markerToHighlight$ = this._store.pipe(select(getMarkerToHighlight));
    this.selectedMarker$ = this._store.pipe(select(getSelectedMarker));
    this.snackbarSuccess$ = this._store.pipe(select(getSnackbarSuccess), takeUntil(this._unsubscribe$)).subscribe(response => {
      if (!isStringNullOrEmpty(response)) {
        this.snackbarConfirmedAs = response;
        this._snackbarService.open(this._snackbarSuccess, 4000);
        this._changeDetectorRef.markForCheck();
      }
    });
    this.snackbarError$ = this._store.pipe(select(getSnackbarError), takeUntil(this._unsubscribe$)).subscribe(response => {
      if (response) {
        this._snackbarService.open(this._snackbarError, 4000);
      }
    });
    this.vehicleFuelTypes$ = this._store.pipe(select(getVehicleFuelTypesFromHierarchy));

    this._store
      .pipe(
        select(getContextMenu),
        filter(menuData => !isElementNull(menuData)),
        takeUntil(this._unsubscribe$)
      )
      .subscribe(menuData => this.showMarkerContextMenu(menuData));
  }

  private _getStartLocationObservable(): Observable<LatLng[]> {
    return this._store.pipe(
      select(getShowStartingLocation),
      filter(startLocation => !isElementNull(startLocation)),
      take(1),
      switchMap(startLocation => {
        switch (startLocation) {
          case EStartLocationSource.FromUserSettings: {
            return this.userSettings$.pipe(
              filter(userSettings => !isElementNull(userSettings)),
              take(1),
              map(userSettings => [new LatLng(userSettings.StartLocation.Latitude, userSettings.StartLocation.Longitude)])
            );
          }
          case EStartLocationSource.FromUserPlots: {
            return this._store.pipe(
              select(getMapChangeBounds),
              filter(latLngs => !isElementNull(latLngs)),
              take(1),
              map(latLngs => latLngs.map(l => new LatLng(l.lat, l.lng)))
            );
          }
        }
      })
    );
  }

  private _setUpMarkers(): Observable<MarkerInfo[]> {
    return this.isReplayOpen$.pipe(
      switchMap(isReplayOpen => (isReplayOpen ? this._getReplayMarkers() : this._getMarkersFilterByFeature()))
    );
  }

  private _getReplayMarkers(): Observable<ReplayMarkerInfo[]> {
    const replay$ = this._store.pipe(
      select(getShouldAnimatePlots),
      filter(shouldAnimatePlots => shouldAnimatePlots !== null),
      switchMap(shouldAnimatePlots =>
        iif(
          () => shouldAnimatePlots,
          this.replayMarkers$.pipe(
            filter(plots => plots.length > 0),
            concatMap(replayMarkers => this._playReplayAnimation(replayMarkers))
          ),
          this.replayMarkers$.pipe(debounceTime(50))
        )
      ),
      map((markers: ReplayMarkerInfo[]) => this._addClassToMarkers(markers, 'custom-marker-replay'))
    );

    const geofence$ = this._getGeofencePlots();
    return combineLatest([replay$, geofence$]).pipe(map(([replay, geofence]) => [...replay, ...geofence]));
  }

  private _getGeofencePlots(): Observable<MarkerInfo[]> {
    return combineLatest([
      this.geofenceMarkers$,
      this.hotspotsMarkers$,
      this.isSuggestedGeofenceEnabled$,
      this.isGeofenceEnabled$,
      this.maxPlottableMarkers$
    ]).pipe(
      map(([geofenceMarkers, hotspotsMarkers, isSuggestedGeofenceEnabled, isGeofenceEnabled, maxPlottableMarkers]) => {
        let markers: MarkerInfo[] = [];
        markers = this._addMarkersByFeature(
          <boolean>isSuggestedGeofenceEnabled,
          markers,
          <MarkerInfo[]>hotspotsMarkers,
          <IMaxPlottableMarkers[]>maxPlottableMarkers
        );

        markers = this._addMarkersByFeature(
          <boolean>isGeofenceEnabled,
          markers,
          <MarkerInfo[]>geofenceMarkers,
          <IMaxPlottableMarkers[]>maxPlottableMarkers
        );

        return markers;
      })
    );
  }

  private _addClassToMarkers(markers: ReplayMarkerInfo[], classNameToAdd: string): ReplayMarkerInfo[] {
    return markers.map(marker => {
      let className = classNameToAdd;
      if (marker.options.className) {
        className = `${className} ${marker.options.className}`;
      }
      return { ...marker, options: { ...marker.options, className } };
    });
  }

  private _setClassToMarkers(markers: ReplayMarkerInfo[], className: string): ReplayMarkerInfo[] {
    return markers.map(m => ({
      ...m,
      options: {
        ...m.options,
        className
      }
    }));
  }

  private _playReplayAnimation(replayMarkers: ReplayMarkerInfo[]): Observable<ReplayMarkerInfo[]> {
    const invisibleMarkers: ReplayMarkerInfo[] = this._setClassToMarkers(replayMarkers, 'custom-marker--invisible');

    const updateFrequency = this._getUpdateFrequencyMs(replayMarkers);
    return merge(
      of(invisibleMarkers),
      timer(1000, updateFrequency).pipe(
        take(replayMarkers.length),
        switchMap(i => {
          let markers = of(replayMarkers.slice(0, i + 1).concat(invisibleMarkers.slice(i + 1)));

          // if last marker end animation
          if (i === replayMarkers.length - 1) {
            markers = markers.pipe(
              tap(() => {
                this._store.dispatch(new EndReplayPlotAnimation());
              })
            );
          }
          return markers;
        })
      )
    );
  }

  private _getUpdateFrequencyMs(replayMarkers: ReplayMarkerInfo[]): number {
    const markersLengthToAnimationTime = new Map<number, number>([
      [20, 2000],
      [60, 3000],
      [100, 4000],
      [140, 5000],
      [200, 6000],
      [300, 7000],
      [400, 8000],
      [500, 9000],
      [600, 10000]
    ]);
    // find the nearest length to the replayMarkers.length to get total animation time
    let difference = Number.MAX_VALUE;
    let totalAnimationTime = 0;
    markersLengthToAnimationTime.forEach((animationTime, length) => {
      const currentDifference = Math.abs(length - replayMarkers.length);
      if (currentDifference <= difference) {
        difference = currentDifference;
        totalAnimationTime = animationTime;
      }
    });
    // get the total animation time and caclulate update frequency;
    const updateFrequency = Math.floor(totalAnimationTime / replayMarkers.length);
    return updateFrequency;
  }

  private _addMarkersByFeature(
    isFeatureEnabled: boolean,
    allMarkers: MarkerInfo[],
    markersToAdd: MarkerInfo[],
    maxPlottableMarkers: IMaxPlottableMarkers[]
  ): MarkerInfo[] {
    if (isFeatureEnabled) {
      allMarkers = this._addMarkersIfNotOverTheLimit(allMarkers, markersToAdd, maxPlottableMarkers);
    } else {
      allMarkers = this._addMarkersToAlwaysAdd(allMarkers, markersToAdd);
    }
    return allMarkers;
  }

  private _isOverTheLimitForType(markers: MarkerInfo[], maxPlottableMarkers: IMaxPlottableMarkers[]): boolean {
    if (markers.length === 0) {
      return false;
    }

    const maxPlottableForType = maxPlottableMarkers.find(maxInfo => maxInfo.type === markers[0].type);
    if (isElementNull(maxPlottableForType)) {
      return false;
    }

    return markers.length > maxPlottableForType.max;
  }

  private _addMarkersIfNotOverTheLimit(
    allMarkers: MarkerInfo[],
    markersToAdd: MarkerInfo[],
    maxPlottableMarkers: IMaxPlottableMarkers[]
  ): MarkerInfo[] {
    if (this._isOverTheLimitForType(markersToAdd, maxPlottableMarkers)) {
      this.isReplayOpen$
        .pipe(
          take(1),
          filter(isReplayOpen => !isReplayOpen)
        )
        .subscribe(() => {
          this.showSnackbar();
        });

      allMarkers = this._addMarkersToAlwaysAdd(allMarkers, markersToAdd);
    } else {
      allMarkers = allMarkers.concat([...markersToAdd]);
    }

    return allMarkers;
  }

  private _addMarkersToAlwaysAdd(allMarkers: MarkerInfo[], markersToAdd: MarkerInfo[]): MarkerInfo[] {
    const markersToAlwaysAdd = markersToAdd.filter(m => m.shouldAlwaysBePlotted);
    if (markersToAlwaysAdd.length > 0) {
      allMarkers = allMarkers.concat([...(<MarkerInfo[]>markersToAlwaysAdd)]);
    }
    return allMarkers;
  }

  private _transformLabelMarker(label: ILabel): MarkerLabel {
    if (!label) {
      return undefined;
    }

    const marker: MarkerLabel = {
      id: label.id,
      data: label.customElements
    };

    return marker;
  }

  private _setupReplayAnimationState(): void {
    combineLatest([this.animationState$, this.isReplayOpen$])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([toState, isReplayOpen]) => {
        this.replayAnimationState = toState;

        // the goal is add the panel element only when animation of hiding elements on the map is completed
        if (isReplayOpen && (toState === EMapAnimationState.replayPanelOpen || toState === EMapAnimationState.replayPanelClosed)) {
          // set to true only if it's false
          if (!this.showReplaySidePanel) {
            this.showReplaySidePanel = true;
          }
        } else if (!isReplayOpen && this.showReplaySidePanel) {
          this.showReplaySidePanel = false;
        }
      });
  }

  private _getCombinedPlotIconType(iconName: string, plotType: EPlotType): ECombinedPlotType {
    if (iconName.indexOf('charging') > -1 && !iconName.endsWith('charging')) {
      return ECombinedPlotType.chargingDeviceWithIssue;
    }

    switch (plotType) {
      case EPlotType.asset:
        return ECombinedPlotType.assetWithIssue;
      case EPlotType.poweredAsset:
        return ECombinedPlotType.poweredAssetWithIssue;
      case EPlotType.vehicle:
      default:
        return ECombinedPlotType.vehicleWithIssue;
    }
  }

  private _getMarkersFilterByFeature(): Observable<MarkerInfo[]> {
    return combineLatest([
      this.hotspotsMarkers$,
      this.devicesIssues$,
      this.vehicleMarkers$,
      this.geofenceMarkers$,
      this.garminStopMarkers$,
      this.workOrdersMarkers$,
      this.harshDrivingEventMarker$,
      this.assetsMarkers$,
      this.isSuggestedGeofenceEnabled$,
      this.isGeofenceEnabled$,
      this.isGarminStopsEnabled$,
      this.isWorkOrdersEnabled$,
      this.maxPlottableMarkers$,
      this.showPlotIconsWithError$
    ]).pipe(
      map(
        ([
          hotspotsMarkers,
          deviceIssues,
          vehicleMarkers,
          geofenceMarkers,
          garminStopsMarkers,
          workOrdersMarkers,
          harshDrivingEventMarker,
          assetsMarkers,
          isSuggestedGeofenceEnabled,
          isGeofenceEnabled,
          isGarminStopsEnabled,
          isWorkOrdersEnabled,
          maxPlottableMarkers,
          showPlotIconsWithError
        ]) => {
          let markers: MarkerInfo[] = [];
          var devicesWithIssues = <IDeviceHealth[]>deviceIssues;
          markers = <MarkerInfo[]>vehicleMarkers;
          markers = markers.concat([...(<MarkerInfo[]>assetsMarkers)]);
          if (showPlotIconsWithError) {
            this._updateMarkersForDevicesWithIssues(markers, devicesWithIssues);
          }

          markers = this._addMarkersByFeature(
            <boolean>isSuggestedGeofenceEnabled,
            markers,
            <MarkerInfo[]>hotspotsMarkers,
            <IMaxPlottableMarkers[]>maxPlottableMarkers
          );

          markers = this._addMarkersByFeature(
            <boolean>isGeofenceEnabled,
            markers,
            <MarkerInfo[]>geofenceMarkers,
            <IMaxPlottableMarkers[]>maxPlottableMarkers
          );

          markers = this._addMarkersByFeature(
            <boolean>isGarminStopsEnabled,
            markers,
            <MarkerInfo[]>garminStopsMarkers,
            <IMaxPlottableMarkers[]>maxPlottableMarkers
          );

          markers = this._addMarkersByFeature(
            <boolean>isWorkOrdersEnabled,
            markers,
            <MarkerInfo[]>workOrdersMarkers,
            <IMaxPlottableMarkers[]>maxPlottableMarkers
          );

          if (!isElementNull(<MarkerInfo>harshDrivingEventMarker)) {
            markers.push(<MarkerInfo>harshDrivingEventMarker);
          }

          return markers;
        }
      )
    );
  }

  private _updateMarkersForDevicesWithIssues(markers: MarkerInfo[], devicesWithIssues: IDeviceHealth[]): void {
    if (isArrayNullOrEmpty(markers) || isArrayNullOrEmpty(devicesWithIssues)) {
      return;
    }

    markers.forEach(x => {
      // Update or remove this plot type check if we need to extend to other plot types
      if (x.type !== EPlotType.vehicle && x.type !== EPlotType.asset && x.type !== EPlotType.poweredAsset) {
        return;
      }

      if (
        devicesWithIssues.find((y: IDeviceHealth) => y.vehicleId === x.id.toString()) &&
        x.options.iconName.indexOf(EPlotIcon.DeviceWithIssue) === -1
      ) {
        x.combinedPlotType = this._getCombinedPlotIconType(x.options.iconName, x.type);
        x.options.iconName = `${x.options.iconName}-ic-${EPlotIcon.DeviceWithIssue}`;
      }
    });
  }

  private _loadReplayWhenDriverPanelLoaded(): void {
    this.isVehicleDriverPanelLoaded$
      .pipe(
        filter(vehicleDriverPanelLoaded => isPlatformBrowser(this._platformId) && vehicleDriverPanelLoaded),
        take(1)
      )
      .subscribe(() => {
        this._lazyLoadModuleService.load('replay').then(_ => {
          this._store.dispatch(new ReplayModuleLoaded());
        });
        this._store.dispatch(new GetReplayUserSettings());
      });
  }

  private _enableClusteringOnMap() {
    this.plotClusterConfig$.next(this.clusterConfig);
  }

  private _disableClusteringOnMap() {
    this.plotClusterConfig$.next(new Map<MarkerClustererOptions, EPlotType[]>());
  }
}
